import { PageHero, CTABand } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Truck, Users, PackageCheck, Warehouse, ClipboardList } from "lucide-react";

const SERVICES = [
  { icon: Truck, title: "Local & Long Distance", desc: "Whether it's across Ashburn or across the country, your household goods travel with the same crew accountability and care from door to door." },
  { icon: Users, title: "Employee Relocation", desc: "Corporate relocation programs that support your relocating employees from first box to final placement — policy administration, coordination, and white-glove execution." },
  { icon: PackageCheck, title: "Packing & Unpacking", desc: "Full-service packing, custom crating for valuables and art, and complete unpack-and-place service so your new home works on day one." },
  { icon: Warehouse, title: "Short-Term Storage", desc: "Secure, climate-conscious storage between homes — vaulted, inventoried, and delivered when you're ready." },
  { icon: ClipboardList, title: "Inventory Management", desc: "Every item barcoded, catalogued, and tracked. You always know what moved, what's stored, and what arrives next." },
];

const STEPS = [
  { num: "01", title: "In-Home Survey", desc: "A relocation specialist documents your home, timeline, and special-care items." },
  { num: "02", title: "Fixed Proposal", desc: "One transparent quote covering packing, transport, storage, and placement." },
  { num: "03", title: "Move Day Execution", desc: "Uniformed crew, protected floors and doorways, and a single point of contact." },
  { num: "04", title: "Placement & Follow-Up", desc: "Furniture placed, boxes unpacked, debris removed, and a post-move walkthrough." },
];

export default function Residential() {
  return (
    <main data-testid="residential-page">
      <PageHero
        eyebrow="Residential Moving"
        title="Home, handled with care"
        subtitle="Local and long distance residential moving, employee relocation, and secure storage — delivered with commercial-grade planning."
      />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08}
              className="group bg-white border border-slate-200 rounded-md p-8 hover:border-brand hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <s.icon size={30} className="text-brand mb-5 group-hover:scale-110 transition-transform" />
              <h3 className="font-display font-semibold text-xl text-navy mb-2">{s.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] font-semibold text-brand mb-3">How It Works</p>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-navy mb-12">
              Four steps to a stress-free move
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.08} className="bg-white p-8">
                <p className="font-display text-brand font-bold text-sm mb-3">{s.num}</p>
                <h3 className="font-display font-semibold text-lg text-navy mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTABand />
    </main>
  );
}
